import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, CheckCircle2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { CONTACT } from "@/lib/constants";
import Seo from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ nom: "", entreprise: "", telephone: "", courriel: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.courriel || !form.message) {
      toast.error("Veuillez compléter les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message envoyé ! Nous vous répondons sous 24 h.");
    } catch {
      toast.error("Erreur d'envoi. Veuillez réessayer.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description="Contactez Passerelle Numérique Inc. à Montréal. Téléphone 514 443 8084, courriel contact@passerellenumerique.ca. Devis gratuit sous 24 h."
        path="/contact"
      />
      <section className="bg-white pt-16 pb-12">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="text-[#00B8D9] text-xs tracking-[0.2em] uppercase font-bold mb-3">Contact</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-[#0B1F3A] tracking-tighter max-w-3xl text-balance">
            Parlons de votre <span className="text-[#00B8D9]">prochain projet</span>
          </h1>
          <p className="mt-5 text-lg text-[#4B5563] max-w-2xl">
            Une question, un projet, un devis ? Notre équipe vous répond sous 24 heures.
          </p>
        </div>
      </section>

      <section className="bg-[#F5F7FA] py-16">
        <div className="mx-auto max-w-6xl px-6 md:px-10 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <Phone className="h-5 w-5 text-[#00B8D9] mb-2" />
              <div className="font-heading font-bold text-[#0B1F3A]">Téléphone</div>
              <a href={CONTACT.phoneHref} className="text-[#4B5563] hover:text-[#00B8D9]" data-testid="contact-phone-link">{CONTACT.phone}</a>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <Mail className="h-5 w-5 text-[#00B8D9] mb-2" />
              <div className="font-heading font-bold text-[#0B1F3A]">Courriel</div>
              <a href={`mailto:${CONTACT.email}`} className="text-[#4B5563] hover:text-[#00B8D9] break-all" data-testid="contact-email-link">{CONTACT.email}</a>
            </div>
            <div className="bg-[#0B1F3A] text-white rounded-2xl p-6">
              <MapPin className="h-5 w-5 text-[#00B8D9] mb-2" />
              <div className="font-heading font-bold">Localisation</div>
              <div className="text-gray-300 text-sm">{CONTACT.city}</div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl p-8 border border-gray-200">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="contact-success">
                <CheckCircle2 className="h-16 w-16 text-[#00B8D9] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Message envoyé !</h3>
                <p className="text-[#4B5563]">Nous vous répondons sous 24 heures. Merci pour votre confiance.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Nom *</Label><Input data-testid="contact-input-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required /></div>
                  <div><Label>Entreprise</Label><Input data-testid="contact-input-entreprise" value={form.entreprise} onChange={(e) => setForm({ ...form, entreprise: e.target.value })} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Téléphone</Label><Input data-testid="contact-input-tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></div>
                  <div><Label>Courriel *</Label><Input data-testid="contact-input-courriel" type="email" value={form.courriel} onChange={(e) => setForm({ ...form, courriel: e.target.value })} required /></div>
                </div>
                <div><Label>Message *</Label><Textarea rows={6} data-testid="contact-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required /></div>
                <Button type="submit" disabled={loading} className="w-full bg-[#00B8D9] hover:bg-[#0096B3] text-white font-semibold py-6" data-testid="contact-submit">
                  {loading ? "Envoi..." : <><Send className="h-4 w-4 mr-2" /> Envoyer le message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
